import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IPagination } from '@models';
import { Clear } from '@mui/icons-material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  TextField
} from '@mui/material';
import { MAIN_THEME } from '@styles';

import FormFieldWrapper from './FormFieldWrapper';
import { IFormFieldInputBaseProps } from '.';

import clsx from 'clsx';
import { debounce, isEmpty } from 'lodash';
import { makeStyles } from 'tss-react/mui';

export interface IMultiAutocompleteFormFieldProps
  extends IFormFieldInputBaseProps {
  value: string[];
  loader?: boolean;
  freeSolo?: boolean;
  onChange?: (value: string[]) => void;
  onBlur?: (value: string[]) => void;
  classes?: { input?: string; wrapper?: string };
  maxItemsDisplayed?: number;
  canClearValue?: boolean;
  fetchOptions: (
    query: string,
    pagination: IPagination
  ) => Promise<{
    options: Record<string, string>;
    totalCount: number;
  }>;
  fetchOptionsByValues?: (
    values: string[],
    pagination: IPagination
  ) => Promise<{
    options: Record<string, string>;
    totalCount: number;
  }>;
}

const useStyles = makeStyles<{
  error?: string;
}>()((theme, { error }) => ({
  inputContainerStyles: {
    width: '100%',
    marginBottom: '5px'
  },
  inputWrapper: {
    position: 'relative'
  },
  inputStyles: {
    width: '100%',
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px',
    outline: 'none',
    '& .MuiInputBase-input': {
      fontFamily: MAIN_THEME.font.regular,
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm
      }
    },
    '& .MuiInputBase-root': {
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      minHeight: '35px',
      width: '100%',
      padding: '0 25px 0 15px!important',
      margin: '0 auto',
      backgroundColor: MAIN_THEME.color.white98,
      outlineColor: error ? theme.palette.error.main : undefined,
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `0px solid ${MAIN_THEME.color.white92}`,
      boxSizing: 'border-box',
      borderRadius: '5px',
      '&::before': {
        display: 'none'
      },
      '&::after': {
        display: 'none'
      }
    }
  },
  optionDesign: {
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    lineHeight: '15px',
    color: MAIN_THEME.color.black,
    fontFamily: MAIN_THEME.font.regular
  },
  paginationIcons: {
    display: 'flex',
    gap: '5px',
    justifyContent: 'end',
    alignItems: 'center',
    fontSize: MAIN_THEME.fontSize.sm,
    padding: '5px 7px 0 0',
    '& .MuiSvgIcon-root': {
      width: '0.8em'
    },
    '& .MuiIconButton-root': {
      padding: '2px'
    }
  },
  chipStyle: {
    height: 'unset',
    color: MAIN_THEME.color.white,
    backgroundColor: MAIN_THEME.color.black,
    borderRadius: '0px',
    margin: '3px',
    '& .MuiChip-deleteIcon': {
      color: 'white !important'
    }
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 10px)',
    right: '3%',
    opacity: 1,
    height: '25px',
    width: '25px',
    fontFamily: MAIN_THEME.font.regular
  },
  selectMaterial: {
    '& .MuiInput-input': {
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      color: MAIN_THEME.color.black25
    },
    '& .MuiInputBase-root:before': {
      border: 'none',
      backgroundColor: 'unset',
      borderRadius: '0',
      borderBottom: `1px solid ${MAIN_THEME.color.white92}`
    },

    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${MAIN_THEME.color.white92}`
      }
  },
  selectError: {
    '& .MuiInputBase-root:before': {
      borderBottom: `1px solid ${theme.palette.error.main}`
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${theme.palette.error.main}`
      },
    '& .MuiInputBase-root:focus': {
      borderColor: theme.palette.error.main,
      outlineColor: theme.palette.error.main,
      boxShadow: 'none',
      borderRadius: 0
    },
    '& .MuiInputBase-root': {
      borderColor: `${theme.palette.error.main}`,
      outlineColor: `${theme.palette.error.main}`
    }
  },
  clearBtnContainer: {
    marginRight: '10px'
  },
  clearBtn: {
    cursor: 'pointer',
    height: '12px',
    width: '12px'
  }
}));

export interface IAutocompletePagination {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

function getPaginationRange(
  pageNumber: number,
  pageSize: number,
  totalCount: number
) {
  const from = (pageNumber - 1) * pageSize + 1;
  const to = Math.min(pageNumber * pageSize, totalCount);

  return `${from} – ${to} of ${totalCount}`;
}

const MultiAutocompleteFormField: React.FC<
  IMultiAutocompleteFormFieldProps
> = ({
  name,
  title,
  placeholder,
  className,
  style,
  classes = {},
  value = [], // FIXED: Always ensure value is an array
  error,
  onChange,
  onBlur,
  disabled,
  loader,
  inputOnly,
  freeSolo = false,
  material,
  maxItemsDisplayed = 10,
  fetchOptions,
  fetchOptionsByValues,
  canClearValue = false
}) => {
  const { classes: inputClasses } = useStyles({ error });
  const [options, setOptions] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<IAutocompletePagination>({
    pageNumber: 1,
    pageSize: maxItemsDisplayed,
    totalCount: 0
  });
  const [inputValue, setInputValue] = useState('');

  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // FIXED: Ensure initial load happens
  useEffect(() => {
    debouncedGetOptions('', {
      pageNumber: 1,
      pageSize: maxItemsDisplayed,
      totalCount: 0
    });
  }, []);

  const debouncedGetOptions = useCallback(
    debounce(
      (inputValue: string, paginationValues: IAutocompletePagination) => {
        setLoading(true);
        fetchOptions(inputValue, {
          pageSize: paginationValues.pageSize,
          pageNumber: paginationValues.pageNumber
        })
          .then(({ options: newOptions, totalCount }) => {
            setOptions((prevOptions) => {
              const selectedOptions = valueRef.current.reduce(
                (acc: Record<string, string>, val) => {
                  if (prevOptions[val]) {
                    acc[val] = prevOptions[val];
                  }

                  return acc;
                },
                {}
              );

              return {
                ...newOptions,
                ...selectedOptions
              };
            });
            setPagination({
              ...paginationValues,
              totalCount
            });
          })
          .finally(() => {
            setLoading(false);
          });
      },
      300
    ),
    [fetchOptions]
  );

  useEffect(() => {
    if (Array.isArray(value) && value.length > 0 && fetchOptionsByValues) {
      const fetchInitialOptions = async () => {
        const missingValues = value.filter((val) => !options[val]);
        if (missingValues.length > 0) {
          setLoading(true);
          try {
            const fetchedOptions = await fetchOptionsByValues(
              missingValues,
              pagination
            );
            setOptions((prevOptions) => ({
              ...prevOptions,
              ...fetchedOptions.options
            }));
          } finally {
            setLoading(false);
          }
        }
      };

      fetchInitialOptions();
    }
  }, [value, fetchOptionsByValues, options]);

  // FIXED: Better clear handling
  const handleClearValue = () => {
    onChange?.([]);
    setInputValue('');
    // Fetch fresh options after clearing
    debouncedGetOptions('', {
      pageNumber: 1,
      pageSize: maxItemsDisplayed,
      totalCount: 0
    });
  };

  const renderInput = () => {
    const availableOptionsCount = Object.keys(options).length;
    const selectedCount = value?.length || 0;
    const unselectedOptionsCount = Math.max(
      0,
      availableOptionsCount - selectedCount
    );

    return (
      <div className={inputClasses.inputContainerStyles}>
        <FormControl
          error={!!error}
          disabled={!!disabled}
          className={inputClasses.inputContainerStyles}
          size="small"
          variant="outlined"
        >
          <Autocomplete
            freeSolo={freeSolo}
            multiple={true}
            disabled={!!disabled}
            disablePortal={false}
            options={Object.keys(options)}
            onInputChange={(event, newInputValue) => {
              if (newInputValue !== inputValue) {
                setInputValue(newInputValue);
                if (event?.type !== 'click') {
                  debouncedGetOptions(newInputValue, {
                    pageNumber: 1,
                    pageSize: maxItemsDisplayed,
                    totalCount: pagination.totalCount
                  });
                }
                if (isEmpty(newInputValue)) {
                  debouncedGetOptions('', {
                    pageNumber: 1,
                    pageSize: maxItemsDisplayed,
                    totalCount: pagination.totalCount
                  });
                }
              }
            }}
            onBlur={() => {
              const invalidValues = value.filter(
                (val) => !options[val] && !isEmpty(val)
              );
              if (invalidValues.length > 0) {
                setInputValue('');
                onChange?.(value.filter((val) => options[val]));
                debouncedGetOptions('', {
                  pageNumber: 1,
                  pageSize: maxItemsDisplayed,
                  totalCount: pagination.totalCount
                });
              }
              onBlur?.(value);
            }}
            onChange={(event, newValue) => {
              onChange?.(newValue);
            }}
            value={value || []} // FIXED: Ensure never undefined
            getOptionLabel={(option) => options[option] || option}
            disableClearable={true}
            selectOnFocus
            loading={loading}
            noOptionsText={loading ? 'Fetching data...' : 'No data'}
            renderOption={(props, option, state) => {
              // FIXED: Extract key to avoid spreading it
              const { key, ...liProps } = props;
              const showPagination =
                unselectedOptionsCount < pagination.totalCount &&
                state.index === unselectedOptionsCount - 1 &&
                (selectedCount === 0 || !isEmpty(inputValue));

              return (
                <React.Fragment key={key || option}>
                  <li {...liProps}>
                    <span className={inputClasses.optionDesign}>
                      {options[option]}
                    </span>
                  </li>
                  {showPagination && (
                    <Box className={inputClasses.paginationIcons}>
                      {getPaginationRange(
                        pagination.pageNumber,
                        pagination.pageSize,
                        pagination.totalCount
                      )}
                      <IconButton
                        size="small"
                        disabled={pagination.pageNumber === 1 || loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          const pageNumber = Math.max(
                            pagination.pageNumber - 1,
                            1
                          );
                          debouncedGetOptions(inputValue, {
                            pageSize: pagination.pageSize,
                            pageNumber,
                            totalCount: pagination.totalCount
                          });
                        }}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          const pageNumber = pagination.pageNumber + 1;
                          debouncedGetOptions(inputValue, {
                            pageSize: pagination.pageSize,
                            pageNumber,
                            totalCount: pagination.totalCount
                          });
                        }}
                      >
                        <NavigateNextIcon />
                      </IconButton>
                    </Box>
                  )}
                </React.Fragment>
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              Array.isArray(tagValue) &&
              (tagValue as readonly string[]).map(
                (option: string, index: number) => {
                  // FIXED: Extract key prop before spreading
                  const tagProps = getTagProps({ index });
                  const { key, ...restTagProps } = tagProps;

                  return !isEmpty(options[option]) ? (
                    <Chip
                      key={key || `${option}-${index}`}
                      label={options[option]}
                      {...restTagProps}
                      className={inputClasses.chipStyle}
                    />
                  ) : (
                    <Chip
                      key={key || `${option}-${index}`}
                      label={option}
                      {...restTagProps}
                      className={inputClasses.chipStyle}
                    />
                  );
                }
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                placeholder={placeholder}
                className={clsx(
                  !material && inputClasses.inputStyles,
                  material && inputClasses.selectMaterial,
                  error && inputClasses.selectError
                )}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: params.InputProps.startAdornment,
                  endAdornment: (
                    <>
                      {(value?.length ?? 0) > 0 && canClearValue && (
                        <InputAdornment
                          position="end"
                          className={inputClasses.clearBtnContainer}
                        >
                          <IconButton
                            aria-label="clear date"
                            onClick={handleClearValue}
                            edge="end"
                            size="small"
                            style={{ marginRight: material ? '30px' : '-17px' }}
                          >
                            <Clear className={inputClasses.clearBtn} />
                          </IconButton>
                        </InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
                variant="standard"
              />
            )}
          />
        </FormControl>
      </div>
    );
  };

  return inputOnly ? (
    renderInput()
  ) : (
    <>
      <FormFieldWrapper
        title={title}
        error={error}
        name={name}
        className={clsx(classes.wrapper, className)}
        style={style}
      >
        {renderInput()}
        {loader && (
          <CircularProgress
            className={inputClasses.loader}
            size={20}
            thickness={3}
          />
        )}
      </FormFieldWrapper>
    </>
  );
};

export default MultiAutocompleteFormField;
